// src/pages/teacher/TeacherCirculars.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import {
  Card, Button, Badge, Spinner, Row, Col, Form, Alert, InputGroup, Container
} from "react-bootstrap";
import {
  collection, doc, getDoc, getDocs, onSnapshot, query, where, setDoc, serverTimestamp, limit
} from "firebase/firestore";
import { db } from "../../services/firebase";
import { useAuth } from "../../context/AuthContext";

/* ---------------- theme helpers ---------------- */
const cssVar = (name, fb = "") =>
  (typeof window !== "undefined" &&
    getComputedStyle(document.documentElement).getPropertyValue(name).trim()) || fb;

const useBrand = () => {
  const read = () => ({
    primary: cssVar("--brand-primary", "#0d6efd"),
    accent:  cssVar("--brand-accent",  "#6f42c1"),
    surface: cssVar("--surface", "#fff"),
  });
  const [brand, setBrand] = useState(read);
  useEffect(() => {
    const upd = () => setBrand(read());
    upd();
    const obs = new MutationObserver(upd);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-bs-theme","style","class"] });
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    mq?.addEventListener?.("change", upd);
    return () => { obs.disconnect(); mq?.removeEventListener?.("change", upd); };
  }, []);
  return brand;
};
const hexToRgba = (hex, a = 1) => {
  const h = String(hex || "").replace("#", "");
  const [r, g, b] = h.length === 3
    ? [parseInt(h[0]+h[0],16), parseInt(h[1]+h[1],16), parseInt(h[2]+h[2],16)]
    : [parseInt(h.slice(0,2),16), parseInt(h.slice(2,4),16), parseInt(h.slice(4,6),16)];
  return `rgba(${r||0},${g||0},${b||0},${a})`;
};

/* ---------------- helpers ---------------- */
const toJS = (t) => (t?.toDate ? t.toDate() : t ? new Date(t) : null);
const fmtAt = (t) => {
  const d = toJS(t);
  return d && dayjs(d).isValid() ? dayjs(d).format("DD MMM YYYY, h:mm A") : "—";
};
const fmtDate = (t) => {
  const d = toJS(t);
  return d && dayjs(d).isValid() ? dayjs(d).format("DD MMM YYYY") : "—";
};
const uniqBy = (arr, key = (x) => x.id) => {
  const m = new Map();
  for (const x of arr) m.set(key(x), x);
  return [...m.values()];
};
const norm = (s) => String(s || "").trim().toLowerCase();
const toU = (s) => String(s || "").trim().toUpperCase();

const parseList = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String).map(s=>s.trim()).filter(Boolean);
  return String(raw).split(/[,\s/|]+/g).map(s=>s.trim()).filter(Boolean);
};
const parseClassList = (raw) => {
  // keep "Class 10" intact; split only on commas/pipes/slashes
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String).map(s=>s.trim()).filter(Boolean);
  return String(raw).split(/[,\|/]+/g).map(s=>s.trim()).filter(Boolean);
};
const getTeacherClasses = (t) => {
  const ids = parseClassList(t?.classIds);
  if (ids.length) return ids;
  const cls = parseClassList(t?.classes || t?.classNames);
  if (cls.length) return cls;
  return t?.className ? [String(t.className)] : [];
};
const getSectionsForClass = (t, cls) => {
  if (!t) return [];
  if (t.sectionsByClass && t.sectionsByClass[cls]) return parseList(t.sectionsByClass[cls]).map(toU);
  const flat = parseList(t.sections).map(toU);
  if (flat.length) return flat;
  const single = parseList(t.section).map(toU);
  if (single.length) return single;
  return [];
};

/** Icons by category/priority */
const ICONS = {
  general: "bi-megaphone",
  exam: "bi-journal-check",
  fee: "bi-cash-coin",
  holiday: "bi-calendar2-week",
  event: "bi-calendar-event",
  sports: "bi-trophy",
  urgent: "bi-exclamation-triangle",
  default: "bi-bell",
};
const iconFor = (category, priority) => {
  if (norm(priority) === "urgent") return ICONS.urgent;
  return ICONS[norm(category)] || ICONS.default;
};

export default function TeacherCirculars() {
  const brand = useBrand();
  const { user, profile } = useAuth();

  const schoolId = profile?.schoolId || null;
  const teacherIdFromProfile = profile?.teacherId || null;

  // teacher profile
  const [teacher, setTeacher] = useState(null);
  const [loadingTeacher, setLoadingTeacher] = useState(true);

  // active class/section (NEW: like Assignments/Attendance)
  const teacherClasses = useMemo(() => getTeacherClasses(teacher), [teacher]);
  const [activeClass, setActiveClass] = useState("");
  const [sectionsAvail, setSectionsAvail] = useState([]);
  const [activeSectionU, setActiveSectionU] = useState(""); // "" => All Sections

  useEffect(() => {
    const first = teacherClasses[0] || "";
    setActiveClass(first);
    setSectionsAvail(getSectionsForClass(teacher, first));
    setActiveSectionU("");
  }, [teacher?.id]); // eslint-disable-line

  useEffect(() => {
    const secs = getSectionsForClass(teacher, activeClass);
    setSectionsAvail(secs);
    if (secs.length && !secs.includes(activeSectionU)) setActiveSectionU("");
  }, [activeClass, teacher]); // eslint-disable-line

  // circulars
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // per-circular read status for this teacher
  const [reads, setReads] = useState({}); // circularId -> {readAt, ...}
  const readUnsubsRef = useRef({});

  // filters / search
  const [q, setQ] = useState("");
  const [fCategory, setFCategory] = useState("all");
  const [fPriority, setFPriority] = useState("all"); // all|normal|high|urgent
  const [fStatus, setFStatus] = useState("all"); // all|active|expired|upcoming|unread
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const teacherName = teacher?.name || profile?.displayName || (user?.email || "").split("@")[0] || "Teacher";
  const isActive = norm(teacher?.status) !== "inactive";

  /* ---------- resolve teacher profile (by teacherId; fallback email) ---------- */
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!schoolId || !user?.uid) {
        setTeacher(null); setLoadingTeacher(false); return;
      }
      setLoadingTeacher(true);
      try {
        if (teacherIdFromProfile) {
          const snap = await getDoc(doc(db, "schools", schoolId, "teachers", teacherIdFromProfile));
          if (!cancelled && snap.exists()) { setTeacher({ id: snap.id, ...(snap.data()||{}) }); setLoadingTeacher(false); return; }
        }
        if (user?.email) {
          const qs = await getDocs(
            query(collection(db, "schools", schoolId, "teachers"), where("email", "==", String(user.email).toLowerCase()), limit(1))
          );
          if (!cancelled && !qs.empty) { const d=qs.docs[0]; setTeacher({ id: d.id, ...(d.data()||{}) }); setLoadingTeacher(false); return; }
        }
      } catch {}
      if (!cancelled) { setTeacher(null); setLoadingTeacher(false); }
    }
    load();
    return () => { cancelled = true; };
  }, [schoolId, teacherIdFromProfile, user?.uid, user?.email]);

  const classTag = useMemo(() => {
    const secLabel = activeSectionU ? `-${activeSectionU}` : (sectionsAvail.length ? "-ALL" : "");
    return [activeClass || teacher?.className, secLabel ? secLabel.replace(/^-/, "") : teacher?.section].filter(Boolean).join("-");
  }, [activeClass, activeSectionU, sectionsAvail.length, teacher?.className, teacher?.section]);

  /* ---------- subscribe to circulars (multi-class/section aware) ---------- */
  useEffect(() => {
    // clear old read listeners when query set changes
    Object.values(readUnsubsRef.current).forEach((u) => u && u());
    readUnsubsRef.current = {};
    setReads({});

    if (!schoolId || !activeClass) { setItems([]); setLoading(false); return; }
    if (!isActive) { setItems([]); setLoading(false); return; }

    setLoading(true);
    const col = collection(db, "schools", schoolId, "circulars");
    const bucket = new Map();
    const merge = (docs) => {
      docs.forEach((d) => bucket.set(d.id, { id: d.id, ...(d.data()||{}) }));
      const arr = Array.from(bucket.values())
        .sort((a,b)=>(toJS(b.publishedAt||b.createdAt) - toJS(a.publishedAt||a.createdAt)));
      setItems(arr);
      setLoading(false);
    };
    const unsubs = [];

    // Base: class match only
    unsubs.push(onSnapshot(
      query(col, where("className", "==", activeClass)),
      (snap) => merge(snap.docs),
      () => setLoading(false)
    ));

    if (activeSectionU) {
      // Specific section selected: include array-contains + single-field matches (+ 'all'/'')
      try {
        unsubs.push(onSnapshot(
          query(col, where("className", "==", activeClass), where("sections", "array-contains", activeSectionU)),
          (snap) => merge(snap.docs),
          () => setLoading(false)
        ));
      } catch {}
      try {
        unsubs.push(onSnapshot(
          query(col, where("className", "==", activeClass), where("section", "==", activeSectionU)),
          (snap) => merge(snap.docs),
          () => setLoading(false)
        ));
      } catch {}
      try {
        unsubs.push(onSnapshot(
          query(col, where("className", "==", activeClass), where("section", "in", ["", "all"])),
          (snap) => merge(snap.docs),
          () => setLoading(false)
        ));
      } catch {}
    } else {
      // All Sections: subscribe each of the teacher's sections (if any) + rows for all/blank
      const secs = sectionsAvail.length ? sectionsAvail : [];
      secs.forEach((s) => {
        try {
          unsubs.push(onSnapshot(
            query(col, where("className", "==", activeClass), where("sections", "array-contains", s)),
            (snap) => merge(snap.docs),
            () => setLoading(false)
          ));
        } catch {}
        try {
          unsubs.push(onSnapshot(
            query(col, where("className", "==", activeClass), where("section", "==", s)),
            (snap) => merge(snap.docs),
            () => setLoading(false)
          ));
        } catch {}
      });
      try {
        unsubs.push(onSnapshot(
          query(col, where("className", "==", activeClass), where("section", "in", ["", "all"])),
          (snap) => merge(snap.docs),
          () => setLoading(false)
        ));
      } catch {}
    }

    return () => unsubs.forEach((u)=>u && u());
  }, [schoolId, isActive, activeClass, activeSectionU, sectionsAvail.join("|")]);

  /* ---------- subscribe to teacher's read marker per circular ---------- */
  useEffect(() => {
    const teacherReadId = teacher?.id || user?.uid || null;
    if (!schoolId || !teacherReadId || items.length === 0) return;
    const active = readUnsubsRef.current;

    items.forEach((c) => {
      const ref = doc(db, "schools", schoolId, "circulars", c.id, "reads", teacherReadId);
      if (active[c.id]) return;
      active[c.id] = onSnapshot(ref, (snap) => {
        setReads((prev) => ({ ...prev, [c.id]: snap.exists() ? { id: snap.id, ...snap.data() } : null }));
      });
    });

    const ids = new Set(items.map((x) => x.id));
    Object.keys(active).forEach((cid) => {
      if (!ids.has(cid)) {
        active[cid]?.(); delete active[cid];
        setReads((prev) => { const copy = { ...prev }; delete copy[cid]; return copy; });
      }
    });
  }, [items, schoolId, teacher?.id, user?.uid]);

  /* ---------- computed filters ---------- */
  const categories = useMemo(() => {
    const s = new Set();
    items.forEach((it) => {
      const cat = (it.category || "General").trim();
      if (cat) s.add(cat);
    });
    return ["all", ...Array.from(s).sort((a, b) => a.localeCompare(b))];
  }, [items]);

  const filtered = useMemo(() => {
    const now = new Date();
    return items
      .filter((c) => {
        const title = String(c.title || "").toLowerCase();
        const body = String(c.message || c.description || c.body || "").toLowerCase();
        const cat = String(c.category || "General").trim();
        const pri = String(c.priority || c.importance || "normal").toLowerCase();
        const pub = toJS(c.publishedAt || c.createdAt);
        const till = toJS(c.validTill || c.expiryAt || c.expiresAt);
        const unread = !reads[c.id];

        // status calc
        const active = (!till || now <= till) && (!!pub ? now >= pub : true);
        const expired = !!till && now > till;
        const upcoming = !!pub && now < pub;

        if (q.trim()) {
          const needle = q.trim().toLowerCase();
          const hay = [title, body, cat.toLowerCase(), pri].join(" ");
          if (!hay.includes(needle)) return false;
        }
        if (fCategory !== "all" && cat !== fCategory) return false;
        if (fPriority !== "all" && pri !== fPriority) return false;

        if (fStatus !== "all") {
          if (fStatus === "active" && !active) return false;
          if (fStatus === "expired" && !expired) return false;
          if (fStatus === "upcoming" && !upcoming) return false;
          if (fStatus === "unread" && !unread) return false;
        }

        if (fromDate && pub && dayjs(pub).isBefore(dayjs(fromDate).startOf("day"))) return false;
        if (toDate && pub && dayjs(pub).isAfter(dayjs(toDate).endOf("day"))) return false;

        // final guard: respect currently selected class/section on each record
        if (c.className !== activeClass) return false;
        const secSingle = toU(c.section || "");
        const secList = Array.isArray(c.sections) ? c.sections.map(toU) : [];
        const allSec = secSingle === "" || secSingle === "ALL" || secList.includes("ALL");
        if (activeSectionU) {
          const ok = allSec || secSingle === activeSectionU || secList.includes(activeSectionU);
          if (!ok) return false;
        } else if (sectionsAvail.length) {
          const mine = sectionsAvail;
          const ok = allSec || mine.includes(secSingle) || secList.some(s => mine.includes(s));
          if (!ok) return false;
        }
        return true;
      })
      .sort((a, b) => {
        // Newest first; urgent before others if same time
        const ta = toJS(a.publishedAt || a.createdAt) || new Date(0);
        const tb = toJS(b.publishedAt || b.createdAt) || new Date(0);
        if (tb.getTime() !== ta.getTime()) return tb - ta;
        const pa = norm(a.priority || a.importance || "normal");
        const pb = norm(b.priority || b.importance || "normal");
        const w = (p) => (p === "urgent" ? 3 : p === "high" ? 2 : 1);
        return w(pb) - w(pa);
      });
  }, [items, q, fCategory, fPriority, fStatus, fromDate, toDate, reads, activeClass, activeSectionU, sectionsAvail]);

  /* ---------- actions ---------- */
  const onOpen = (url) => { if (!url) return; window.open(url, "_blank", "noopener,noreferrer"); };

  const markRead = async (c) => {
    const readId = teacher?.id || user?.uid || "";
    if (!schoolId || !readId || !c?.id) return;
    try {
      const ref = doc(db, "schools", schoolId, "circulars", c.id, "reads", readId);
      await setDoc(ref, {
        teacherId: teacher?.id || "",
        className: activeClass || "",
        section: activeSectionU || "",
        readAt: serverTimestamp(),
      }, { merge: true });
    } catch (e) {
      console.error(e);
    }
  };

  /* ---------- UI ---------- */
  if (loadingTeacher) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="py-5 text-center text-muted">
          <Spinner animation="border" size="sm" /> Loading your profile…
        </Card.Body>
      </Card>
    );
  }

  if (!teacherClasses.length) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="py-4">
          <div className="d-flex align-items-center gap-2 mb-2">
            <i className="bi bi-exclamation-triangle text-warning fs-5" />
            <div className="fw-semibold">Your profile doesn’t have any classes mapped.</div>
          </div>
          <div className="text-muted">
            Ask the school admin to add <b>classIds/classes</b> (and optional <b>sections</b>) on your teacher record.
          </div>
        </Card.Body>
      </Card>
    );
  }

  const teacherNameLabel = teacher?.name || profile?.displayName || (user?.email || "").split("@")[0] || "Teacher";
  const inactive = norm(teacher?.status) === "inactive";

  return (
    <Container fluid className="p-0">
      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <div className="d-flex flex-column">
          <h5 className="mb-0">Circulars & Notices</h5>
          <small className="text-muted">
            {teacherNameLabel} • Class {activeClass} • {activeSectionU ? `Section ${activeSectionU}` : "All Sections"} {inactive ? " • Inactive" : ""}
          </small>
        </div>
      </div>

      {/* Class/Section selectors (NEW) + Filter Bar */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body className="py-3">
          <Row className="g-2 align-items-end">
            <Col xs={12} sm={6} md={3}>
              <Form.Label className="mb-1">Class</Form.Label>
              <Form.Select value={activeClass} onChange={(e)=>setActiveClass(e.target.value)}>
                {teacherClasses.map((c) => (
                  <option key={c} value={c}>{`Class ${c}`}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Form.Label className="mb-1">Section</Form.Label>
              <Form.Select value={activeSectionU} onChange={(e)=>setActiveSectionU(toU(e.target.value))}>
                <option value="">All Sections</option>
                {sectionsAvail.map((s)=>(
                  <option key={s} value={s}>{`Section ${s}`}</option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} md={6} lg={3}>
              <Form.Label className="mb-1">Search</Form.Label>
              <InputGroup>
                <InputGroup.Text className="bg-light"><i className="bi bi-search" /></InputGroup.Text>
                <Form.Control
                  placeholder="Search title / message / category"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </InputGroup>
            </Col>

            <Col xs={6} md={3} lg={2}>
              <Form.Label className="mb-1">Category</Form.Label>
              <Form.Select value={fCategory} onChange={(e) => setFCategory(e.target.value)}>
                {categories.map((s) => (
                  <option key={s} value={s}>{s === "all" ? "All categories" : s}</option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={6} md={3} lg={2}>
              <Form.Label className="mb-1">Priority</Form.Label>
              <Form.Select value={fPriority} onChange={(e) => setFPriority(e.target.value)}>
                <option value="all">Any priority</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </Form.Select>
            </Col>

            <Col xs={6} md={3} lg={2}>
              <Form.Label className="mb-1">Status</Form.Label>
              <Form.Select value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="upcoming">Upcoming</option>
                <option value="expired">Expired</option>
                <option value="unread">Unread</option>
              </Form.Select>
            </Col>

            <Col xs={6} md={2} lg={1}>
              <Form.Label className="mb-1">From</Form.Label>
              <Form.Control type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </Col>
            <Col xs={6} md={2} lg={1}>
              <Form.Label className="mb-1">To</Form.Label>
              <Form.Control type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </Col>

            <Col xs={12} md={2} lg={1} className="d-grid">
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setQ(""); setFCategory("all"); setFPriority("all");
                  setFStatus("all"); setFromDate(""); setToDate("");
                }}
              >
                Reset
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Inactive notice */}
      {!isActive ? (
        <Alert variant="warning" className="mb-3">
          Your teacher account is currently <b>inactive</b>. Please contact your school admin.
        </Alert>
      ) : null}

      {/* List / Loading */}
      <Card className="border-0 shadow-sm">
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" /> Loading circulars…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-muted py-4 text-center">No circulars found.</div>
          ) : (
            <Row className="g-3">
              {filtered.map((c) => {
                const title = c.title || "Circular";
                const body = c.message || c.description || c.body || "";
                const cat = c.category || "General";
                const pri = c.priority || c.importance || "Normal";
                const pub = toJS(c.publishedAt || c.createdAt);
                const till = toJS(c.validTill || c.expiryAt || c.expiresAt);
                const isUnread = !reads[c.id];

                const ico = iconFor(cat, pri);
                const hasLink = !!(c.link || c.fileURL || c.attachmentUrl);
                const tag = [c.className || "", c.section || (Array.isArray(c.sections) ? c.sections.join(",") : "")]
                  .filter(Boolean).join("-");

                return (
                  <Col key={c.id} xs={12} md={6} lg={4}>
                    <Card
                      className={`h-100 shadow-sm border-0 circular-card ${norm(pri)}`}
                      style={{ borderColor: hexToRgba(brand.primary, 0.22), position: "relative", overflow: "hidden" }}
                    >
                      <div className="card-particles" aria-hidden />

                      <Card.Body className="d-flex flex-column">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="d-flex align-items-center gap-3">
                            <div className="subj-ico d-inline-flex align-items-center justify-content-center" title={cat}>
                              <i className={`bi ${ico}`} />
                            </div>
                            <div className="fw-semibold text-truncate" title={title}>
                              {title}
                            </div>
                          </div>
                          {isUnread ? <Badge bg="primary">New</Badge> : <Badge bg="secondary">Read</Badge>}
                        </div>

                        <div className="text-muted small">
                          Category: <span className="fw-semibold">{cat}</span> • Priority: <span className="fw-semibold">{String(pri).toUpperCase()}</span>
                        </div>

                        <div className="small mt-2 text-truncate" title={body}>{body}</div>

                        <div className="text-muted small mt-2">
                          Published: <span className="fw-semibold">{fmtAt(pub)}</span>
                          {till ? <> &nbsp;• Valid till: <span className="fw-semibold">{fmtDate(till)}</span></> : null}
                        </div>

                        <div className="mt-2">
                          <StatusBadges c={c} />
                        </div>

                        <div className="mt-auto pt-3 d-flex justify-content-between align-items-center">
                          <Badge bg="light" text="dark" className="border">{tag || "—"}</Badge>
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              variant={isUnread ? "success" : "outline-success"}
                              onClick={() => markRead(c)}
                            >
                              <i className="bi bi-check2-circle me-1" />
                              Mark read
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              disabled={!hasLink}
                              onClick={() => onOpen(c.link || c.fileURL || c.attachmentUrl)}
                            >
                              <i className="bi bi-box-arrow-up-right me-1" />
                              Open
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Styles */}
      <style>{`
        .circular-card {
          border-radius: 18px;
          background: linear-gradient(
            180deg,
            ${hexToRgba(brand.primary, 0.06)} 0%,
            transparent 60%
          );
          transition: transform .18s ease, box-shadow .18s ease;
        }
        .circular-card:hover { transform: translateY(-2px); box-shadow: 0 .5rem 1.2rem rgba(0,0,0,.12); }
        .circular-card.urgent { box-shadow: 0 .35rem .9rem ${hexToRgba("#dc3545", .22)}; }
        .circular-card.high { box-shadow: 0 .35rem .9rem ${hexToRgba("#ffc107", .22)}; }

        .subj-ico {
          width: 58px; height: 58px; border-radius: 16px;
          background: ${hexToRgba(brand.primary, 0.18)};
          color: ${brand.primary};
          border: 1px solid ${hexToRgba(brand.primary, 0.30)};
          font-size: 30px;
        }
        @media (max-width: 576px) {
          .subj-ico { width: 50px; height: 50px; font-size: 26px; }
        }

        .card-particles {
          position: absolute; inset: 0; pointer-events: none; z-index: 0;
          background-image:
            radial-gradient(${hexToRgba(brand.primary, .18)} 1px, transparent 1px),
            radial-gradient(${hexToRgba(brand.accent, .18)} 1px, transparent 1px);
          background-position: 0 0, 12px 12px;
          background-size: 24px 24px, 24px 24px;
          animation: drift 9s linear infinite;
          opacity: .22;
        }
        .circular-card.urgent .card-particles { animation-duration: 6.5s; opacity: .26; }
        .circular-card.high .card-particles { animation-duration: 7.5s; opacity: .24; }

        @keyframes drift {
          0% { background-position: 0 0, 12px 12px; }
          50% { background-position: 12px 6px, 0 18px; }
          100% { background-position: 0 0, 12px 12px; }
      `}</style>
    </Container>
  );
}

/* inline helper for status/priority badges (kept simple) */
function StatusBadges({ c }) {
  const norm = (s) => String(s || "").trim().toLowerCase();
  const toJS = (t) => (t?.toDate ? t.toDate() : t ? new Date(t) : null);
  const pri = norm(c.priority || c.importance || "normal");
  const pub = toJS(c.publishedAt || c.createdAt);
  const till = toJS(c.validTill || c.expiryAt || c.expiresAt);
  const now = new Date();
  const active = (!till || now <= till) && (!!pub ? now >= pub : true);
  const expired = !!till && now > till;
  const upcoming = !!pub && now < pub;

  return (
    <div className="d-flex flex-wrap gap-2">
      {pri === "urgent" ? <Badge bg="danger">Urgent</Badge> :
       pri === "high" ? <Badge bg="warning" text="dark">High</Badge> :
       <Badge bg="secondary">Normal</Badge>}
      {upcoming ? <Badge bg="info" text="dark">Upcoming</Badge> : null}
      {active ? <Badge bg="success">Active</Badge> : null}
      {expired ? <Badge bg="dark">Expired</Badge> : null}
    </div>
  );
}
